import React from "react";
import {
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import tables from "../../shared/Tables/table.module.scss";
import DownloadIcon from "static/svgs/Download";
import { Search } from "@mui/icons-material";

const getSearchText = (row, searchKeys) => {
  if (typeof searchKeys === "function") {
    return searchKeys(row);
  }
  if (!Array.isArray(searchKeys) || searchKeys.length === 0) {
    return "";
  }
  return searchKeys.map((key) => (row?.[key] ?? "").toString()).join(" ");
};

const ensureAllOption = (options) => {
  if (!Array.isArray(options)) return ["all"];
  return options.includes("all") ? options : ["all", ...options];
};

const FilterableTable = ({
  title,
  data = [],
  columns = [],
  searchKeys = [],
  searchPlaceholder = "Search",
  filters = [],
  emptyMessage = "No results found.",
  rowsPerPageOptions = [5, 10, 25],
  initialRowsPerPage = 5,
  classNames = {},
  getRowKey = (row, index) => row?.id ?? index,
  onRowClick,
  showFilterToggle = false,
  defaultShowFilters = true,
  headerActions,
  downloadFilename = "table-data.csv",
}) => {
  const [search, setSearch] = React.useState("");
  const [filterValues, setFilterValues] = React.useState(() => {
    const initial = {};
    filters.forEach((filter) => {
      initial[filter.id] = filter.defaultValue ?? "all";
    });
    return initial;
  });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
  const [showFilters, setShowFilters] = React.useState(defaultShowFilters);

  const filteredRows = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return data.filter((row) => {
      const matchesSearch = normalizedSearch
        ? getSearchText(row, searchKeys)
            .toLowerCase()
            .includes(normalizedSearch)
        : true;
      const matchesFilters = filters.every((filter) => {
        const selected = filterValues[filter.id] ?? "all";
        if (selected === "all") return true;
        if (typeof filter.filterFn === "function") {
          return filter.filterFn(row, selected);
        }
        const rowValue = filter.valueAccessor
          ? filter.valueAccessor(row)
          : row?.[filter.id];
        return rowValue === selected;
      });
      return matchesSearch && matchesFilters;
    });
  }, [data, search, searchKeys, filters, filterValues]);

  const pagedRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (filterId, value) => {
    setFilterValues((prev) => ({ ...prev, [filterId]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilterValues((prev) => {
      const reset = { ...prev };
      Object.keys(reset).forEach((key) => {
        reset[key] = "all";
      });
      return reset;
    });
    setPage(0);
  };

  const hasActiveFilters =
    search.trim().length > 0 ||
    Object.values(filterValues).some((value) => value !== "all");

  const showFiltersRow =
    filters.length > 0 && (!showFilterToggle || showFilters);

  return (
    <Card className={classNames.card} variant="outlined" elevation={0}>
      <div className={classNames.tableHeader}>
        <Typography className={classNames.sectionTitle} fontWeight={700}>
          {title}
        </Typography>
        <div className={classNames.tableHeaderActions}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            InputProps={{
              sx: { height: 40, borderRadius: 2 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={handleSearchChange}
            className={classNames.searchField}
          />
          {/* {enableDownload ? ( */}
          <IconButton className={tables.iconBtn} sx={{ mx: 1 }}>
            <DownloadIcon />
          </IconButton>
          {/* ) : null} */}
          {headerActions}
          {showFilterToggle ? (
            <button
              type="button"
              className={classNames.filterToggle}
              onClick={() => setShowFilters((prev) => !prev)}
              variant="outlined"
              endIcon={<FilterListOutlinedIcon fontSize="small" />}
            >
              Filters <FilterListOutlinedIcon fontSize="small" />
            </button>
          ) : null}
        </div>
      </div>
      {showFiltersRow ? (
        <div className={classNames.filterRow}>
          <div className={classNames.filterGroup}>
            <span className={classNames.filterLabel}>Filters:</span>
            {filters.map((filter) => {
              const options = ensureAllOption(filter.options);
              return (
                <FormControl
                  key={filter.id}
                  size="small"
                  className={classNames.filterSelect}
                >
                  <InputLabel id={`${filter.id}-label`}>
                    {filter.label}
                  </InputLabel>
                  <Select
                    labelId={`${filter.id}-label`}
                    value={filterValues[filter.id] ?? "all"}
                    label={filter.label}
                    onChange={(event) =>
                      handleFilterChange(filter.id, event.target.value)
                    }
                    sx={{ minWidth: 180, ...filter.selectSx }}
                  >
                    {options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {filter.formatOption
                          ? filter.formatOption(option)
                          : option === "all"
                          ? "All"
                          : option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            })}
          </div>
          {hasActiveFilters ? (
            <button
              type="button"
              className={classNames.clearFilters}
              onClick={handleClearFilters}
            >
              Clear All
            </button>
          ) : null}
        </div>
      ) : null}
      <TableContainer className={classNames.tableContainer}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  className={column.headClassName ?? classNames.tableHead}
                  align={column.align}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className={classNames.emptyState}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, index) => (
                <TableRow
                  key={getRowKey(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  hover={Boolean(onRowClick)}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={column.cellClassName}
                      align={column.align}
                    >
                      {column.renderCell
                        ? column.renderCell(row)
                        : row?.[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        className={classNames.tablePagination}
      />
    </Card>
  );
};

export default FilterableTable;
